import { normalizeBillingPayload } from './billingNormalizer'
import { portalToday } from './portalToday'
import { currentSemesterAnchor, isCurrentSemester } from './semesters'

/**
 * Portal data API — all queries go through the portal-data Edge Function.
 *
 * The Edge Function validates the portal token server-side and uses the
 * runtime's service credentials to query the canonical portal backend. This means:
 *   - The publishable key in the JS bundle has ZERO direct table access
 *   - Token validation happens on every data request (not just at login)
 *   - Student scoping is enforced server-side, not client-side
 */

const PROXY_PREFIX = '/api/functions'
const PORTAL_WRITE_QUERIES = new Set(['add_goal'])
const PORTAL_UNCACHED_READS = new Set(['overview', 'billing'])
// ─── Response Cache ──────────────────────────────────────────
// Cache non-money data responses for 5 minutes. Billing and overview bypass
// this cache so a ledger/rollover change is visible on the next read.

const _cache = new Map()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function _cacheKey(name, body) {
  return `${name}:${JSON.stringify(body)}`
}

function _getCached(key) {
  const entry = _cache.get(key)
  if (!entry) return null
  if (Date.now() - entry.ts > CACHE_TTL) {
    _cache.delete(key)
    return null
  }
  return entry.data
}

function _setCache(key, data) {
  _cache.set(key, { data, ts: Date.now() })
}

async function _postJson(url, body, options = {}) {
  const timeoutMs = Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : 0
  const ctrl = timeoutMs > 0 ? new AbortController() : null
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null
  try {
    const resp = await fetch(url, {
      method: 'POST',
      redirect: 'error',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl?.signal,
    })
    const text = await resp.text()
    let data = null
    if (text) {
      try {
        data = JSON.parse(text)
      } catch {
        data = null
      }
    }
    return { resp, data, text }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// #366: the throttle's typed-429 helpers live in ./rateLimited (import-light,
// so page tests that mock this module keep them); re-exported for callers.
export {
  PORTAL_RATE_LIMITED_CODE,
  PORTAL_RATE_LIMITED_MESSAGE,
  isPortalRateLimited,
} from './rateLimited'

function _buildEdgeError(result) {
  const payload = result?.data
  const message =
    payload?.error ||
    payload?.message ||
    result?.text ||
    `${result?.resp?.status || 'request failed'}`
  const err = new Error(message)
  if (result?.resp) {
    err.status = result.resp.status
  }
  if (payload && typeof payload === 'object') {
    if (typeof payload.code === 'string') err.code = payload.code
    if (typeof payload.retry === 'boolean') err.retry = payload.retry
    if (Number.isInteger(payload.retry_after_s)) err.retryAfterS = payload.retry_after_s
    err.payload = payload
  }
  return err
}

function _shouldCacheEdgeCall(name, body, options = {}) {
  if (options.cache === false) return false
  return name === 'portal-data'
    && !PORTAL_WRITE_QUERIES.has(body?.query)
    && !PORTAL_UNCACHED_READS.has(body?.query)
}

function _getEdgeEndpoint(name) {
  // Post-N7B cutover (2026-04-20): browser traffic goes through the
  // same-origin Netlify proxy only. Falling back to the retired
  // Supabase edge host reintroduces split-brain reads for tokens,
  // goals, attendance, and intake telemetry whenever Netlify returns
  // an error, so the browser must fail against the canonical path.
  return `${PROXY_PREFIX}/${name}`
}

export async function callEdgeFunction(name, body, options = {}) {
  const shouldCache = _shouldCacheEdgeCall(name, body, options)
  const key = _cacheKey(name, body)
  if (shouldCache) {
    const cached = _getCached(key)
    if (cached) return cached
  }

  const result = await _postJson(_getEdgeEndpoint(name), body, options)
  if (!result.resp.ok) {
    throw _buildEdgeError(result)
  }

  const data = result.data

  // Never cache a failure envelope. A transport-level 200 can still carry
  // `ok: false` (the degraded assignments route does exactly that), and
  // caching it pinned the section in its degraded state for the full 5
  // minutes AFTER the backend recovered — a self-healing outage turned into
  // a sticky one, with no user action able to clear it.
  if (shouldCache && data?.ok !== false && !data?.error) {
    _setCache(key, data)
  } else if (name === 'portal-data' && PORTAL_WRITE_QUERIES.has(body?.query)) {
    _cache.clear() // Writes happened — invalidate all cached reads
  }

  return data
}

export async function submitIntakeForm(payload) {
  const data = await callEdgeFunction(
    'intake-form-submit', payload, { cache: false }
  )
  if (data?.ok !== true || data?.received !== true) {
    throw _buildEdgeError({
      resp: { status: 200 },
      data,
      text: '',
    })
  }
  return data
}

/**
 * Call the portal-data Edge Function.
 */
async function portalQuery(token, query, studentId, extra = {}) {
  try {
    const data = await callEdgeFunction('portal-data', {
      token, query, student_id: studentId, ...extra,
    })

    if (data?.ok === false || data?.error) {
      return {
        data: null,
        error: data?.error || data?.message || 'request failed',
        code: data?.code || null,
        retry: data?.retry === true,
      }
    }

    return { data, error: null, code: null, retry: false }
  } catch (err) {
    console.error(`Portal query '${query}' exception:`, err)
    return {
      data: null,
      error: err.message,
      code: err.code || null,
      retry: err.retry === true || (!err.status || err.status >= 500),
    }
  }
}

export async function validatePortalToken(token) {
  try {
    const data = await callEdgeFunction(
      'validate-portal-token',
      { token },
      { cache: false }
    )

    const valid = data?.valid ?? data?.ok ?? false
    if (!valid) {
      return {
        data: null,
        error: data?.error || 'invalid portal token',
        code: 'invalid_portal_token',
        retry: data?.retry === true,
      }
    }

    return {
      data: {
        ...data,
        valid: true,
        role: data?.role || 'student',
        student_ids: Array.isArray(data?.student_ids) ? data.student_ids : [],
        class_ids: Array.isArray(data?.class_ids) ? data.class_ids : [],
      },
      error: null,
      code: null,
      retry: false,
    }
  } catch (err) {
    console.error('Portal token validation failed:', err)
    const invalidToken =
      err?.status === 401 ||
      err?.status === 403 ||
      String(err?.code || '').includes('token')
    return {
      data: null,
      error: err.message,
      code: invalidToken ? 'invalid_portal_token' : (err.code || 'portal_auth_unavailable'),
      retry: invalidToken ? false : (err.retry === true || (!err.status || err.status >= 500)),
    }
  }
}

function _normalizeStudentPayload(data, studentId) {
  return {
    id: data?.student?.id || studentId,
    name: data?.student?.name || '',
  }
}

function _normalizeProgressPayload(data) {
  const entries = data?.feedback || []
  const averages = {
    participation: null,
    motivation: null,
    homework: null,
    met_objective: null,
    count: entries.length,
  }

  if (entries.length > 0) {
    let participationSum = 0
    let motivationSum = 0
    let homeworkSum = 0
    let objectiveSum = 0
    let participationCount = 0
    let motivationCount = 0
    let homeworkCount = 0
    let objectiveCount = 0

    for (const entry of entries) {
      if (entry.participation != null) { participationSum += entry.participation; participationCount++ }
      if (entry.motivation != null) { motivationSum += entry.motivation; motivationCount++ }
      if (entry.homework != null) { homeworkSum += entry.homework; homeworkCount++ }
      if (entry.met_objective != null) { objectiveSum += entry.met_objective; objectiveCount++ }
    }

    averages.participation = participationCount > 0 ? participationSum / participationCount : null
    averages.motivation = motivationCount > 0 ? motivationSum / motivationCount : null
    averages.homework = homeworkCount > 0 ? homeworkSum / homeworkCount : null
    averages.met_objective = objectiveCount > 0 ? objectiveSum / objectiveCount : null
  }

  const feedbackEntries = entries.map(entry => ({
    id: entry.id,
    // classId joins to the schedule classes map for the display-name
    // fallback chain; className stays the feedback/homework matcher key.
    classId: entry.sessions?.classes?.id ?? null,
    className: entry.sessions?.classes?.name || 'Unknown Class',
    // Parallel display field. An absent key (older function version) is
    // the same as SQL NULL — null, never undefined.
    classDisplayName: entry.sessions?.classes?.display_name ?? null,
    sessionDate: entry.sessions?.session_date || '',
    sessionNumber: entry.sessions?.session_number || 0,
    participation: entry.participation,
    motivation: entry.motivation,
    homework: entry.homework,
    // 2026-09 not-submitted contract: false = the teacher recorded "not
    // submitted" (homework is null on such rows); null = unset; an absent
    // key (older function version) is the same as SQL NULL — null, never
    // undefined, so the chip can never claim a verdict the server didn't make.
    homeworkSubmitted: entry.homework_submitted ?? null,
    metObjective: entry.met_objective,
    feedbackText: entry.feedback_text || '',
    // ebc91c93 session header facts. Absent keys (older function version)
    // degrade to empty/null — deploy-order safe in both directions.
    objectives: Array.isArray(entry.sessions?.objectives)
      ? entry.sessions.objectives
      : [],
    sessionSummary: entry.sessions?.session_summary ?? null,
    createdAt: entry.created_at,
  }))

  return { averages, entries: feedbackEntries }
}

function _effectiveSessionTime(session) {
  const rawTime = session?.session_time_display
  const timeDisplay = typeof rawTime === 'string' ? rawTime.trim() : ''
  const rawDuration = session?.session_duration_hours
  const numeric = ['number', 'string'].includes(typeof rawDuration) ? Number(rawDuration) : NaN
  const sessionDurationHours = Number.isFinite(numeric) && numeric > 0 ? numeric : null
  return { timeDisplay, sessionDurationHours }
}

function _normalizeSchedulePayload(data) {
  const roster = data?.roster || []
  const sessions = data?.sessions || []
  const attendance = data?.attendance || []
  const today = portalToday()

  const classes = roster.map(r => ({
    id: r.classes?.id || r.class_id,
    name: r.classes?.name || 'Unknown',
    // Roster role for this student in this class. 'ta' rows are staff
    // shifts: visible in the schedule, excluded from the attendance
    // basis below. Absent on older function payloads (deploy skew) —
    // unknown reads as student/included, like semester provenance.
    role: r.role ?? null,
    // Parallel display field — never a replacement for `name`, which the
    // feedback/homework matchers join on. An absent key (older function
    // version) reads as NULL, so it takes the same fallback chain.
    displayName: r.classes?.display_name ?? null,
    timeDisplay: r.classes?.time_display,
    sessionDurationHours: r.classes?.session_duration_hours ?? null,
    semesterId: r.classes?.semester_id,
    // Feeds the current-semester anchor shared with the homework tiers.
    // Absent on older payloads (deploy skew) — unknown provenance reads as
    // current, never as excluded.
    semesterStartDate: r.classes?.semester_start_date ?? null,
  }))

  const classById = Object.fromEntries(classes.map(c => [c.id, c]))
  const classMap = Object.fromEntries(classes.map(c => [c.id, c.name]))
  const attendanceMap = Object.fromEntries(attendance.map(a => [`${a.session_id}`, a.status]))

  const upcoming = sessions
    .filter(s => s.session_date && s.session_date >= today)
    .map(s => {
      const effective = _effectiveSessionTime(s)
      return {
        id: s.id,
        classId: s.class_id,
        className: classMap[s.class_id] || 'Unknown',
        sessionDate: s.session_date,
        sessionNumber: s.session_number,
        timeDisplay: effective.timeDisplay,
        sessionDurationHours: effective.sessionDurationHours,
      }
    })

  const pastAll = sessions
    .filter(s => s.session_date && s.session_date < today)
    .sort((a, b) => (b.session_date || '').localeCompare(a.session_date || ''))
    .map(s => {
      const cls = classById[s.class_id]
      const effective = _effectiveSessionTime(s)
      return {
        id: s.id,
        classId: s.class_id,
        className: classMap[s.class_id] || 'Unknown',
        sessionDate: s.session_date,
        sessionNumber: s.session_number,
        timeDisplay: effective.timeDisplay,
        sessionDurationHours: effective.sessionDurationHours,
        attendanceStatus: attendanceMap[s.id] || null,
        // Semester provenance rides on the session so the attendance rate can
        // scope to the current term. The rendered history list ignores it.
        semesterId: cls?.semesterId ?? null,
        semesterStartDate: cls?.semesterStartDate ?? null,
      }
    })

  // The display list stays capped at 20 rows. The attendance denominator must
  // NOT inherit that cap, so the basis is counted over the uncapped
  // current-semester set — a term with more than 20 past sessions would
  // otherwise divide by 20.
  const past = pastAll.slice(0, 20)
  // TA-role classes never enter the basis (2026-09-02): a TA shift absence
  // is not this student's attendance. The load-bearing fix is the
  // republished function's server-side filter; this client-side guard is
  // defense-in-depth for payloads that DO carry role (e.g. a future
  // server-filter regression). It cannot cover an OLD function's payload —
  // no role on the wire, nothing to exclude by — so rates stay wrong until
  // the function publishes (function + client ship in one Netlify publish,
  // so that window is CDN/asset skew only). The exclusion happens INSIDE
  // the counting loop — TA sessions keep their vote on the semester anchor
  // (semesters.js: greatest tuple PRESENT), or a student whose only
  // current-term past sessions are TA shifts would anchor on the PRIOR term
  // and render last term's rate as this term's.
  const attendanceBasis = _currentSemesterAttendance(
    pastAll,
    s => (classById[s.classId]?.role ?? null) === 'ta',
  )

  return { classes, upcoming, past, attendanceBasis }
}

const _ATTENDED_STATUSES = new Set(['present', 'makeup'])
const _TRACKED_STATUSES = new Set(['present', 'absent', 'makeup'])

function _currentSemesterAttendance(pastSessions, excludeSession = null) {
  const anchor = currentSemesterAnchor(pastSessions)
  let attended = 0
  let tracked = 0
  for (const session of pastSessions) {
    if (excludeSession && excludeSession(session)) continue
    if (!isCurrentSemester(session, anchor)) continue
    if (!_TRACKED_STATUSES.has(session.attendanceStatus)) continue
    tracked += 1
    if (_ATTENDED_STATUSES.has(session.attendanceStatus)) attended += 1
  }
  return { attended, tracked }
}

function _normalizeGoalsPayload(data) {
  return (data?.goals || []).map(g => ({
    id: g.id,
    goalText: g.goal_text,
    goalType: g.goal_type || 'other',
    status: g.status || 'active',
    setBy: g.set_by || 'teacher',
    progressNotes: g.progress_note || '',
    createdAt: g.created_at,
  }))
}

function _normalizeMessagesPayload(data) {
  return (data?.messages || []).map(m => ({
    id: m.id,
    messageEn: m.final_send_en || m.edited_draft_en || m.ai_draft_en || '',
    messageCn: m.final_send_cn || m.edited_draft_cn || m.ai_draft_cn || '',
    sentAt: m.sent_at,
    contactName: m.contact_name || '',
  }))
}

function _normalizeAssignmentsPayload(data) {
  return data?.assignments || []
}

function _normalizeSyncPayload(data) {
  return {
    status: data?.status || 'unknown',
    lastSuccessAt: data?.last_success_at || null,
    lastAttemptAt: data?.last_attempt_at || null,
    lastSyncMode: data?.last_sync_mode || null,
    failedTables: Array.isArray(data?.failed_tables) ? data.failed_tables : [],
  }
}

function _normalizeOverviewSection(section, fallbackData, normalize) {
  const payload = section && typeof section === 'object' ? section : {}
  const sectionData = Object.prototype.hasOwnProperty.call(payload, 'data')
    ? payload.data
    : fallbackData
  return {
    data: normalize(sectionData),
    error: payload.error || null,
    code: payload.code || null,
    retry: payload.retry === true,
  }
}

function _normalizeBillingSection(section) {
  const payload = section && typeof section === 'object' ? section : {}
  if (payload.error) {
    return {
      data: null,
      error: payload.error,
      code: payload.code || null,
      retry: payload.retry === true,
    }
  }
  try {
    return {
      data: normalizeBillingPayload(payload.data),
      error: null,
      code: null,
      retry: false,
    }
  } catch {
    return {
      data: null,
      error: 'Billing data is unavailable.',
      code: 'billing_contract_invalid',
      retry: false,
    }
  }
}

function _overviewFailure(error, code, retry) {
  return {
    sync: { data: null, error, code, retry },
    student: { data: null, error, code, retry },
    progress: { data: null, error, code, retry },
    schedule: { data: null, error, code, retry },
    homework: { data: [], error, code, retry },
    goals: { data: [], error, code, retry },
    billing: { data: null, error, code, retry },
    messages: { data: [], error, code, retry },
  }
}

export async function fetchAll(studentId, token) {
  const { data, error, code, retry } = await portalQuery(token, 'overview', studentId)
  if (error) {
    return _overviewFailure(error, code, retry)
  }

  return {
    sync: _normalizeOverviewSection(data?.sync, null, sectionData =>
      sectionData ? _normalizeSyncPayload(sectionData) : null
    ),
    student: _normalizeOverviewSection(data?.student, null, sectionData =>
      sectionData ? _normalizeStudentPayload(sectionData, studentId) : null
    ),
    progress: _normalizeOverviewSection(data?.progress, null, sectionData =>
      sectionData ? _normalizeProgressPayload(sectionData) : null
    ),
    schedule: _normalizeOverviewSection(data?.schedule, null, sectionData =>
      sectionData ? _normalizeSchedulePayload(sectionData) : null
    ),
    homework: _normalizeOverviewSection(data?.assignments, [], sectionData =>
      _normalizeAssignmentsPayload(sectionData)
    ),
    goals: _normalizeOverviewSection(data?.goals, [], sectionData =>
      _normalizeGoalsPayload(sectionData)
    ),
    billing: _normalizeBillingSection(data?.billing),
    messages: _normalizeOverviewSection(data?.messages, [], sectionData =>
      _normalizeMessagesPayload(sectionData)
    ),
  }
}

export async function fetchStudentIdentity(studentId, token) {
  const { data, error } = await portalQuery(token, 'student', studentId)
  if (error) return { data: null, error }

  return { data: _normalizeStudentPayload(data, studentId), error: null }
}

export async function fetchStudentProgress(studentId, token) {
  const { data, error } = await portalQuery(token, 'progress', studentId)
  if (error) return { data: null, error }
  return { data: _normalizeProgressPayload(data), error: null }
}

export async function fetchSchedule(studentId, token) {
  const { data, error } = await portalQuery(token, 'schedule', studentId)
  if (error) return { data: null, error }
  return { data: _normalizeSchedulePayload(data), error: null }
}

export async function fetchBilling(studentId, semesterId, token) {
  const extra = semesterId ? { semester_id: semesterId } : {}
  const { data, error, code, retry } = await portalQuery(token, 'billing', studentId, extra)
  if (error) return { data: null, error, code, retry }
  try {
    return {
      data: normalizeBillingPayload(data, semesterId),
      error: null,
      code: null,
      retry: false,
    }
  } catch {
    return {
      data: null,
      error: 'Billing data is unavailable.',
      code: 'billing_contract_invalid',
      retry: false,
    }
  }
}

export async function fetchGoals(studentId, token) {
  const { data, error } = await portalQuery(token, 'goals', studentId)
  if (error) return { data: null, error }
  return { data: _normalizeGoalsPayload(data), error: null }
}

export async function addGoal(studentId, goalText, goalType, setBy, token) {
  const { data, error } = await portalQuery(token, 'add_goal', studentId, {
    goal_text: goalText,
    goal_type: goalType,
    set_by: setBy,
  })
  return { data, error }
}

export async function fetchMessages(contactIds, token, studentId) {
  const { data, error } = await portalQuery(token, 'messages', studentId)
  if (error) return { data: null, error }
  return { data: _normalizeMessagesPayload(data), error: null }
}

/**
 * Standalone assignments read.
 *
 * ALWAYS returns the typed envelope `{data, error, code, retry}`. The old
 * default swallowed a typed failure and handed back a bare `[]`, which any
 * caller would have rendered as "no homework" — the failed-read-is-unknown
 * violation this module exists to prevent. The `preserveError` opt-in that
 * used to guard the honest shape is gone: honest is now the only shape.
 *
 * (Verified 2026-08-12: no caller in the portal today — both tiers read
 * assignments through `fetchAll`. Changing the contract here is therefore
 * free, and leaves no trap for the next caller.)
 */
export async function fetchAssignments(studentId, token) {
  const { data, error, code, retry } = await portalQuery(token, 'assignments', studentId)
  if (error) {
    return { data: [], error, code, retry }
  }

  return {
    data: _normalizeAssignmentsPayload(data),
    error: null,
    code: null,
    retry: false,
  }
}

async function submitPortalUpload(formData) {
  const resp = await fetch('/api/functions/portal-submit-homework', {
    method: 'POST',
    body: formData,
  })

  let data = null
  try {
    data = await resp.json()
  } catch {
    // Non-JSON response falls through to the synthesized error below.
  }
  if (!resp.ok || data?.error) {
    throw new Error(data?.error || `HTTP ${resp.status}`)
  }
  return data
}

export async function submitHomework(assignmentId, studentId, file, token) {
  if (!token) throw new Error('portal token missing')
  const formData = new FormData()
  formData.append('token', token)
  formData.append('assignment_id', assignmentId)
  formData.append('student_id', studentId)
  formData.append('file', file)
  const result = await submitPortalUpload(formData)
  _cache.clear()
  return result
}
